import { useCallback, useMemo } from 'react';

import { tokens } from '@fhs/ui';

import { Alert } from '../alert';
import { HStack } from '../h-stack';
import { Pressable } from '../pressable';
import { Text } from '../text';
import { Toast, useToast as useToastProvider } from '@fhs-legacy/native-base';
import type { ToastProps, _ToastHook, _ToastTemplateProps, _ToastVariantMapType } from './types';

const TOAST_DISPLAY_TIME = 5000;

const ToastVariantMap: _ToastVariantMapType = {
  informative: 'info',
  positive: 'success',
  negative: 'error',
  neutral: undefined,
};

const backgroundColorMap: Record<keyof _ToastVariantMapType, string> = {
  informative: tokens.colors.$houseDark,
  positive: tokens.colors.$success,
  negative: tokens.colors.$error,
  neutral: tokens.colors.$houseDark,
};

const ToastTemplate: React.VFC<_ToastTemplateProps> = ({
  text,
  render,
  testID,
  variant,
  style,
  onClose,
}) => (
  <Pressable
    flexDirection="row"
    flexBasis="100%"
    onPress={onClose}
    accessibilityRole="button"
    accessibilityHint="Close alert"
    paddingX={3}
    testID={testID}
    style={style}
  >
    <Alert
      maxWidth="100%"
      alignSelf="center"
      flexDirection="row"
      status={ToastVariantMap[variant]}
      variant="black"
      accessibilityLiveRegion="polite"
      accessibilityLabel={text}
      accessibilityHint=""
      marginX={0}
      marginBottom={0}
      style={{ backgroundColor: backgroundColorMap[variant] || 'gray' }}
    >
      <HStack space={2} flexShrink={1} flexGrow={1} alignItems="center">
        {variant !== 'neutral' && <Alert.Icon />}
        {render?.() || (
          <Text fontSize="md" fontWeight="medium" color="white" flexShrink={1}>
            {text}
          </Text>
        )}
      </HStack>
    </Alert>
  </Pressable>
);

export const useToast = (
  defaultToastArgs: Partial<Pick<ToastProps, 'duration' | 'placement' | 'rootToast'>> = {}
): _ToastHook => {
  const toastProvider = useToastProvider();
  const toast = defaultToastArgs.rootToast ? Toast : toastProvider;
  const show = useCallback(
    (args: ToastProps) => {
      if (toast.isActive(args.id)) {
        return;
      }

      const toastId = toast.show({
        id: args.id,
        duration: args.duration ?? defaultToastArgs.duration ?? TOAST_DISPLAY_TIME,
        isClosable: true,
        placement: args.placement ?? defaultToastArgs.placement ?? 'top',
        render: () => (
          <ToastTemplate
            text={args.text}
            variant={args.variant || 'neutral'}
            onClose={() => toast.close(toastId)}
            testID={args.testID}
            style={args.style}
            render={args.render}
          />
        ),
      });
    },
    [defaultToastArgs.duration, defaultToastArgs.placement]
  );
  return useMemo(
    () => ({
      show,
    }),
    [show]
  );
};
